import * as React from 'react';
import { Translate } from 'react-localize-redux';
import styled, { keyframes } from 'styled-components';
import { check_password, str_has_capital, str_has_lower, str_has_number, str_has_special, str_is_long } from 'validators-web';
import { useMe } from '../../../atoms/global/users';
import Input from '../../../components_v2/input/Input';
import { InputMode } from '../../../components_v2/input/model/Model';
import check_circle from '../../../images/icons/green_check.svg';
import { DefaultButton } from '../../../styles/global/css/GlobalButton';
import { PrimaryColor, DarkGreySidely, GreenSidely, LightGreySidely2, OrangeSidely, RedSidely } from '../../../styles/global/css/Utils';
import { translateToNode, translateToString } from '../../../styles/global/translate';
import { sendEmail } from '../../auth/password/action';
import { PasswordRestriction } from '../../auth/password/Reset';
import { ToolbarState } from '../../globals/mainPage/mainPage';
import { INPUT_STYLE_SETTINGS, SystemSettingsDropdownWrapper, SystemSettingsWrapper, SystemSettingTitle } from '../subSettings/styles';
import { changePasswordWithVerification } from './actions';

type PasswordType = {
	new_password1: string
	new_password2: string
	old_password: string
	hide_pwd1: boolean
	hide_pwd2: boolean
	hide_old_pwd: boolean
}

type ErrorPassword = {
	new_password1: boolean
	new_password2: boolean
	old_password: boolean
}

const Orange = `color-mix(in oklab, ${OrangeSidely} 30%, white)`;

const DescriptionText = styled.div`
    margin-top: 20px;
    font-size: 12px;
    line-height: 24px;
	min-width: 200px;
`;

const PasswordBlock = styled.div`
	display: flex;
	align-items: center;
	min-width: 408px
`;

const FlexBlock = styled.div`
	display: flex;
`;

const Block = styled.div`
	display: block;
`;

interface StrengthPasswordProps {
	n_bar: number
	strength: number
}

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0px);
  }
`;

const UnrollPassword = styled.div `
	animation: ${fadeIn} 0.3s ease forwards;
`;

const Spacer = styled.div `
	height: 12px;
`;

const Warning = styled.div `
	color: ${RedSidely};
	font-size: 12px;
	max-width: 350px;
`;

const StrengthPassword = styled.div<StrengthPasswordProps>`
	width: 76px;
	height: 6px;
	background-color: ${p => (p.n_bar > (p.strength ?? 0) - 1) && p.n_bar != 1 ? LightGreySidely2 :
		(p.strength ?? 0) <= 2 ? RedSidely :
			(p.strength ?? 0) >= 5 ? GreenSidely : Orange};
	margin: 5px;
	border-radius: 8px;
`;

const Success = styled.img`
	height: 18px;
	margin-right: 10px;
`;

const PwdResetButton = styled.button`
	background: none;
	color: ${PrimaryColor};
	border: none;
	text-decoration: underline;
	text-align: left;
	cursor: pointer;
	width: 350px;
	height: 21px;
	font-size: 11px;
`;

export default function SecuritySettings(props: {
	setToolBarState: (value: ToolbarState) => void
}) {
	const [password, setPassword] = React.useState<PasswordType>({ new_password1: '', new_password2: '', old_password: '', hide_pwd1: true, hide_pwd2: true, hide_old_pwd: true });
	const [errorState, setErrorState] = React.useState<ErrorPassword>({ new_password1: false, new_password2: false, old_password: false });
	const [success, setSuccess] = React.useState<boolean>(false);
	const [emailSent, setEmailSent] = React.useState<boolean>(false);

	const hasCapital = str_has_capital(password.new_password1);
	const hasLower = str_has_lower(password.new_password1);
	const hasSpecial = str_has_special(password.new_password1);
	const hasNumber = str_has_number(password.new_password1);
	const isLong = str_is_long(password.new_password1);
	const checkPassword = check_password(password.new_password1);
	const strength = Number(hasCapital) + Number(hasLower) + Number(hasSpecial) + Number(hasNumber) + Number(isLong);
	const me = useMe();

	function onSubmit() {
		changePasswordWithVerification(password.new_password1, password.old_password)
			.then(_res => {
				setPassword({ new_password1: '', new_password2: '', old_password: '', hide_pwd1: true, hide_pwd2: true, hide_old_pwd: true });
				setSuccess(true);
			})
			.catch(e => {
				console.log(e);
				setErrorState({ ...errorState, old_password: true });
			});
	}

	function onClick(email: string) {
		setEmailSent(true);
		sendEmail(email).catch(console.error);
	}

	React.useEffect(() => {
		props.setToolBarState({
			bottomRightToolbarComponent: <></>
		});
	}, []);

	React.useEffect(() => {
		setErrorState({ ...errorState,
			new_password1: !checkPassword && (password.new_password2.length > 0),
			new_password2: (password.new_password1.length <= password.new_password2.length || !password.new_password1.startsWith(password.new_password2.substring(0, password.new_password1.length)))
				&& password.new_password2.length !== 0
				&& password.new_password1 !== password.new_password2,
			old_password: false,
		});
		if (password.old_password.length >= 1) {
			setSuccess(false);
		}
	}, [password]);

	return <SystemSettingsWrapper>

		<SystemSettingTitle>{translateToNode('login.change_password')}</SystemSettingTitle>
		<SystemSettingsDropdownWrapper>
			{translateToNode('login.old_password')}
			<PasswordBlock>
				<Input
					mode={errorState.old_password ? InputMode.Error : undefined}
					inputStyle={INPUT_STYLE_SETTINGS}
					name='old_password'
					placeholder={translateToString('login.password')}
					type={password?.hide_old_pwd ? 'password' : 'text'}
					value={password?.old_password}
					onChange={v => password && setPassword({ ...password, old_password: v })}
				/>
			</PasswordBlock>
			{errorState.old_password && <Warning>{translateToNode('login.wrong_password_only')}</Warning>}
		</SystemSettingsDropdownWrapper>
		{!!password.old_password && <UnrollPassword>
			<FlexBlock>
				<Block>
					<SystemSettingsDropdownWrapper>
						{translateToNode('login.choose_new_password')}
						<PasswordBlock>
							<Input
								mode={errorState.new_password1 ? InputMode.Error : undefined}
								inputStyle={INPUT_STYLE_SETTINGS}
								name='new_password'
								placeholder={translateToString('login.new_password')}
								type={password?.hide_pwd1 ? 'password' : 'text'}
								value={password?.new_password1}
								onChange={v => password && setPassword({ ...password, new_password1: v })}
							/>
						</PasswordBlock>
						{errorState.new_password1 && <Warning>{translateToNode('login.weak_password')}</Warning>}
					</SystemSettingsDropdownWrapper>
					<Spacer/>
					<SystemSettingsDropdownWrapper>
						{translateToNode('login.confirm_password')}
						<PasswordBlock>
							<Input
								mode={errorState.new_password2 ? InputMode.Error : undefined}
								inputStyle={INPUT_STYLE_SETTINGS}
								name='confirm_password'
								placeholder={translateToString('login.new_password')}
								type={password?.hide_pwd2 ? 'password' : 'text'}
								value={password?.new_password2}
								onChange={v => password && setPassword({ ...password, new_password2: v })}
							/>
						</PasswordBlock>
						{errorState.new_password2 && <Warning>{translateToNode('login.password_not_matching')}</Warning>}
					</SystemSettingsDropdownWrapper>
					<Spacer/>
					<PasswordBlock>
						<StrengthPassword n_bar={1} strength={strength}/>
						<StrengthPassword n_bar={2} strength={strength}/>
						<StrengthPassword n_bar={3} strength={strength}/>
						<StrengthPassword n_bar={4} strength={strength}/>
					</PasswordBlock>
				</Block>
				<DescriptionText>
					<Translate id="login.choose_password_constraint" />
					<PasswordRestriction password={password.new_password1} color={DarkGreySidely} fontSize={'12px'} />
				</DescriptionText>
			</FlexBlock>
			<DefaultButton
				disabled={password.new_password1 !== password.new_password2 || !checkPassword || password.old_password.length == 0}
				onClick={onSubmit}>
				<div style={{ width:'305px' }}>{translateToString('login.modify')}</div>
			</DefaultButton>
		</UnrollPassword> }
		{success && <FlexBlock><Success src={check_circle}/>{translateToNode('login.password_changed')}</FlexBlock>}
		{!emailSent && <PwdResetButton onClick={() => onClick(me.email)}>
			<Translate id="forgotten password" /> ?
		</PwdResetButton>}
		{emailSent && <FlexBlock><Success src={check_circle}/><Translate id="login.link_for_new_password"/></FlexBlock>}
	</SystemSettingsWrapper>;
}
